import { useCallback, useContext } from 'react'
import { Auth } from '../StateManagment/Auth'
import { db } from '../firebase/config'
import { useFirestoreUserByID } from '../firebase/useFirestore'
import activeUpdateModules from '../helpers/updates/activeUpdateModules'
import countUnseenUpdates, { newestVisibleUpdateDate } from '../helpers/updates/countUnseenUpdates'
import updates from '../data/updates.json'

// Hoeveel updates de ingelogde gebruiker nog niet gezien heeft, en de knop om
// ze als gezien te boeken. Voedt de badge op het menu-item 'Updates'.
//
// Neemt `settings` als argument en roept useSettings() NIET zelf aan: elke
// aanroep daarvan opent drie eigen Firestore-listeners, en de zijbalk heeft
// hem al. Zie src/hooks/useSettingsSingleCall.test.js.
//
// De stand komt uit een listener op het eigen Users-document, niet uit de
// Auth-context: die is één keer gelezen bij het inloggen, en dan zou de badge
// na het openen van de pagina blijven staan tot de volgende herlaadactie.
//
// AI-CONTEXT: See docs/features/updates-log.md

/**
 * @param {object} settings - De teruggave van useSettings() van de aanroeper
 * @returns {{unseen: number|null, markSeen: () => Promise<boolean>}}
 *   `unseen` is null zolang de gebruiker of de instellingen nog niet bekend zijn
 */
const useUnseenUpdates = (settings) => {

    // Buiten een Auth.Provider (tests) is de context undefined; dan is er
    // gewoon geen gebruiker en dus geen badge.
    const authContext = useContext(Auth)
    const userId = authContext?.[0]?.ID || ''

    // '' opent geen listener — dat is hier precies de bedoeling.
    const gebruikers = useFirestoreUserByID(userId)
    const gebruiker = Array.isArray(gebruikers) && gebruikers.length > 0 ? gebruikers[0] : null

    const modules = settings?.loaded ? activeUpdateModules(settings) : null
    const gezienTot = gebruiker?.UpdatesSeenAt
    const nieuwste = modules ? newestVisibleUpdateDate(updates, modules) : ''
    const docid = gebruiker?.docid || ''

    const unseen = modules && gebruiker ? countUnseenUpdates(updates, modules, gezienTot) : null

    const markSeen = useCallback(async () => {
        if (!docid || !nieuwste) return false
        if (typeof gezienTot === 'string' && gezienTot >= nieuwste) return false
        await db.collection('Users').doc(docid).update({ UpdatesSeenAt: nieuwste })
        return true
    }, [docid, nieuwste, gezienTot])

    return { unseen, markSeen }
}

export default useUnseenUpdates
